<template>
  <div>
    <div class="flex items-center">
      <Label
        v-if="props.label"
        :required="props.required"
        :error="props.error"
        class="mb-[0.1rem]"
        :disabled="props.disabled"
        >{{ props.label }}</Label
      >

      <VDropdown
        v-if="props.showPopover"
        :autoHide="true"
        :triggers="['hover', 'click']"
        placement="right"
      >
        <button type="button" class="btn mt-1 px-2 text-link-blue">
          <font-awesome-icon icon="question-circle" />
        </button>
        <template #popper>
          <div class="p-2">
            <slot name="popover"></slot>
          </div>
        </template>
      </VDropdown>
    </div>
    <div class="relative">
      <select
        class="block w-full appearance-none rounded-sm border bg-white px-3 py-2 pr-8 leading-tight shadow-sm outline-hidden hover:border-gray-500 focus:ring-2 focus:ring-primary"
        :class="{ 'border-link-red': props.error }"
        :id="props.id"
        v-model="modelValue"
        :disabled="props.disabled"
        ref="inputElement"
      >
        <option
          v-if="props.placeholder || props.name"
          value=""
          :selected="!props.options.length || !modelValue"
          :disabled="true"
        >
          {{ props.placeholder || props.name }}
        </option>
        <option
          v-for="o in props.options"
          :key="o.value"
          :value="o.value"
          :selected="o.value === modelValue"
          :disabled="o.disabled || false"
        >
          {{ o.label }}
        </option>
      </select>
      <div
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
      >
        <svg
          class="h-4 w-4 fill-current"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
        >
          <path
            d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";

export type SelectOption = {
  label: string;
  value: any;
  disabled?: boolean;
};

type Props = {
  id?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  options?: SelectOption[];
  modelValue: any;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  showPopover?: boolean;
  focus?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  id: "",
  name: "",
  label: "",
  placeholder: "",
  options: () => [],
  modelValue: "",
  disabled: false,
  required: false,
  error: false,
  showPopover: false,
  focus: false,
});

const emit = defineEmits(["update:modelValue"]);

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

const inputElement = ref();

onMounted(async () => {
  if (props.focus) {
    await nextTick();
    setTimeout(() => {
      inputElement.value.focus();
    }, 100);
  }
});
</script>

<style></style>
